import type { ReactNode } from 'react';
import { Outlet } from 'react-router';

import type { UserRole } from '@core/types/api';

import { useCurrentUser } from './useCurrentUser';

export interface RequireRoleProps {
  /** Minimum role. Hierarchical: `role="DISPATCHER"` admits ADMIN and OWNER. */
  role: UserRole;
  /** Rendered when the check fails. Defaults to nothing, which is right for a button. */
  fallback?: ReactNode;
  /** Omit to use this as a route element, where it renders an <Outlet />. */
  children?: ReactNode;
}

/**
 * Works both as a route element and inline around a control.
 *
 *     <Route element={<RequireRole role="ADMIN" fallback={<ForbiddenPage />} />}>
 *     <RequireRole role="ADMIN"><Button>Add tracker</Button></RequireRole>
 *
 * This is UX, not security. Every endpoint enforces the same rule server-side; a hidden
 * button stops an honest user taking a pointless action, and stops nobody else.
 */
export function RequireRole({ role, fallback = null, children }: RequireRoleProps) {
  const me = useCurrentUser();

  if (!me.can(role)) return <>{fallback}</>;
  return <>{children ?? <Outlet />}</>;
}
